import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { Clock, Check, ArrowLeft, Repeat, ExternalLink, Loader2, MapPin, Receipt } from 'lucide-react';
import Footer from './Footer';
import { toast } from 'react-hot-toast';
import { useLot } from '../contexts/LotContext';

interface PaymentCompleteProps {}

interface ParkingSession {
  id: string;
  license_plate: string;
  phone: string;
  hours: number;
  amount: number;
  payment_status: string;
  expires_at: string;
  payment_id?: string;
  card_last4?: string;
  payment_method_type?: string;
  auto_renew?: boolean;
  location?: string;
  customer_id?: string;
  email?: string;
  subscription_group_id?: string | null;
}

// The webhook usually lands within a second or two of the redirect; cap the
// wait so a failed delivery still renders the page instead of spinning.
const MAX_PAYMENT_POLLS = 10;
const PAYMENT_POLL_INTERVAL_MS = 2000;

export default function PaymentComplete(props: PaymentCompleteProps) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { lot, settings, isLoading, error } = useLot();
  const [session, setSession] = useState<ParkingSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  // Multi-spot subscriptions: every plate on the same subscription
  const [groupPlates, setGroupPlates] = useState<string[]>([]);
  const location = useLocation();

  const isSubscription = location.search.includes('subscription=true') ||
                        (session?.auto_renew === true && session?.payment_status === 'paid');

  // The manage token is stashed by ParkingForm before the Stripe round trip;
  // it is also emailed with the receipt if this browser no longer has it.
  const manageToken = sessionId ? sessionStorage.getItem(`manageToken:${sessionId}`) : null;
  const manageSpotsUrl = manageToken && lot?.slug ? `/lots/${lot.slug}/manage/${manageToken}` : null;

  // Create a confetti instance that does not use web workers (avoids CSP violations)
  const confettiCannon = confetti.create(undefined, { useWorker: false, resize: true });

  useEffect(() => {
    // Debug subscription status
    if (session) {
      console.log('Session data for subscription check:', {
        id: session.id,
        autoRenew: session.auto_renew,
        paymentStatus: session.payment_status,
        customerId: session.customer_id,
        searchParams: location.search
      });
    }

    // Trigger confetti effect when the component mounts
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confettiCannon({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confettiCannon({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);

    fetchSession();

    return () => clearInterval(interval);
  }, [sessionId]);

  const fetchSession = async (attempt = 0) => {
    if (!sessionId) return;

    try {
      const { data, error } = await supabase
        .from('parking_sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (error) {
        console.error('Supabase error fetching session:', error);
        throw error;
      }

      if (data) {
        console.log('Fetched session data:', {
          id: data.id,
          status: data.payment_status,
          autoRenew: data.auto_renew,
          customerId: data.customer_id,
          paymentId: data.payment_id
        });
        setSession(data);

        // The webhook marks the session paid, so it can land a moment after the
        // redirect. Poll briefly rather than telling the customer it's unpaid.
        if (data.payment_status !== 'paid' && data.payment_status !== 'Yolo' && attempt < MAX_PAYMENT_POLLS) {
          setTimeout(() => fetchSession(attempt + 1), PAYMENT_POLL_INTERVAL_MS);
        }

        // Multi-spot subscription: list every plate covered by this payment
        if (data.subscription_group_id) {
          const { data: siblings } = await supabase
            .from('parking_sessions')
            .select('license_plate')
            .eq('subscription_group_id', data.subscription_group_id)
            .eq('payment_id', data.payment_id || '')
            .order('created_at', { ascending: true });

          if (siblings && siblings.length > 0) {
            setGroupPlates(siblings.map((row) => row.license_plate));
          }
        }

        // Fetch receipt URL for non-subscription payments
        if (data.payment_id && !data.auto_renew) {
          fetchReceiptUrl(data.payment_id);
        }
      } else {
        console.error('No session data found for ID:', sessionId);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReceiptUrl = async (paymentId: string) => {
    try {
      setReceiptLoading(true);
      const stripeAccountParam = lot?.stripe_account_id 
        ? `&stripeAccount=${encodeURIComponent(lot.stripe_account_id)}` 
        : '';
      
      const response = await fetch(
        `/.netlify/functions/get-receipt-url?paymentId=${encodeURIComponent(paymentId)}${stripeAccountParam}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.receiptUrl) {
          setReceiptUrl(data.receiptUrl);
        }
      }
    } catch (error) {
      console.error('Error fetching receipt URL:', error);
    } finally {
      setReceiptLoading(false);
    }
  };

  const goToCustomerPortal = async () => {
    if (!session?.customer_id) {
      console.error('Customer ID missing:', session);
      toast.error('Customer information not found');
      return;
    }

    console.log('Opening customer portal for ID:', session.customer_id);
    setPortalLoading(true);

    try {
      const response = await fetch('/.netlify/functions/customer-portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: session.customer_id,
          returnUrl: window.location.href,
          // Include connect account if this lot uses Stripe Connect
          stripeAccountId: lot?.stripe_account_id || null
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to access subscription portal');
      }

      const { url } = await response.json();
      
      // Redirect to the portal
      window.location.href = url;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error accessing subscription management';
      toast.error(errorMessage);
      setPortalLoading(false);
    }
  };

  // Show loading state if lot data is still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading parking lot...</p>
        </div>
      </div>
    );
  }

  // Show error state if lot data failed to load
  if (error || !lot) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6">
            <MapPin className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Unable to Load Parking Lot
            </h2>
            <p className="text-gray-600 mb-4">
              {error || 'Lot information not available'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 text-center">
          <p className="text-red-600 mb-4">Session not found</p>
          <Link
            to={`/lots/${lot?.slug || 'adeline-street'}`}
            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 space-y-6">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-green-100 flex items-center justify-center rounded-full mb-4">
            <Check className="text-green-600" size={36} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Successful!</h1>
          <p className="mt-2 text-gray-600">
            {isSubscription
              ? 'Your monthly subscription has started'
              : 'Your parking session is now active'}
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          {groupPlates.length > 1 ? (
            <div>
              <strong>License Plates ({groupPlates.length} spots):</strong>
              <ul className="mt-1 space-y-0.5">
                {groupPlates.map((plate) => (
                  <li key={plate} className="text-gray-700">{plate}</li>
                ))}
              </ul>
            </div>
          ) : (
            <p><strong>License Plate:</strong> {session.license_plate}</p>
          )}
          <p>
            <strong>Duration:</strong> {isSubscription 
              ? 'Monthly (Recurring)' 
              : `${session.hours} hours`}
          </p>
          <p>
            <strong>Amount:</strong> $
            {(groupPlates.length > 1 ? session.amount * groupPlates.length : session.amount).toFixed(2)}
            {isSubscription && '/month'}
            {groupPlates.length > 1 && (
              <span className="text-sm text-gray-500">
                {' '}({groupPlates.length} × ${session.amount.toFixed(2)}, one card)
              </span>
            )}
          </p>
          
          <div className="flex items-center gap-2 text-gray-600">
            {isSubscription ? (
              <>
                <Repeat size={16} />
                <p>Renews monthly</p>
              </>
            ) : (
              <>
                <Clock size={16} />
                <p>Expires: {format(new Date(session.expires_at), 'PPp')}</p>
              </>
            )}
          </div>
          
          {session.card_last4 && session.card_last4 !== 'unknown' ? (
            <p className="text-sm text-gray-500">
              Paid with {session.payment_method_type} ending in {session.card_last4}
            </p>
          ) : session.payment_status === 'paid' ? (
            <p className="text-sm text-gray-500">
              Payment completed successfully
            </p>
          ) : null}

          {session.location && (
            <p className="text-sm text-gray-500">
              <strong>Location:</strong> {session.location}
            </p>
          )}

          {isSubscription && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium">Subscription Information</p>
              <p className="mt-1">
                {groupPlates.length > 1
                  ? 'All spots are billed together to one card each month. Use the links below to change plates, add or remove spots, or update your card.'
                  : 'Your card will be automatically charged monthly. You can manage your subscription using the button below.'}
              </p>
            </div>
          )}

          {/* Plate and spot management for multi-spot subscriptions */}
          {session.subscription_group_id && manageSpotsUrl && (
            <Link
              to={manageSpotsUrl}
              className="w-full bg-white border-2 border-blue-600 text-blue-600 py-2 px-4 rounded-lg hover:bg-blue-50 transition duration-200 flex items-center justify-center gap-2"
            >
              <Repeat size={16} />
              Manage Your Spots
            </Link>
          )}

          {isSubscription && session.customer_id && (
            <button
              onClick={goToCustomerPortal}
              disabled={portalLoading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {portalLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <ExternalLink size={16} />
              )}
              {portalLoading ? 'Loading...' : 'Manage Your Subscription'}
            </button>
          )}

          {/* Receipt button for hourly payments */}
          {!isSubscription && (receiptUrl || receiptLoading) && (
            <a
              href={receiptUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition duration-200 flex items-center justify-center gap-2 ${
                receiptLoading ? 'opacity-50 pointer-events-none' : ''
              }`}
            >
              {receiptLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
              ) : (
                <Receipt size={16} />
              )}
              {receiptLoading ? 'Loading Receipt...' : 'View Receipt'}
            </a>
          )}
        </div>

        <Link
          to={`/lots/${lot?.slug || 'adeline-street'}`}
          className="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
        >
          <ArrowLeft size={16} />
          Return to Home
        </Link>

        <Footer />
      </div>
    </div>
  );
}